import React, { useRef, useState, useCallback } from 'react';
import { BrowserMultiFormatReader, BarcodeFormat, DecodeHintType } from '@zxing/library';
import { FaBarcode } from 'react-icons/fa';

const scannerDialogStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
  border: '1px solid #e0e0e0',
  width: '500px',
  padding: '16px',
  zIndex: 2000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const videoContainerStyle = {
  width: '450px',
  height: '300px',
  background: '#222',
  borderRadius: 8,
  overflow: 'hidden',
  position: 'relative',
};

const barcodeFrameStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'calc(100% - 40px)',
  height: '150px',
  border: '2px solid #00ff00',
  borderRadius: '8px',
  boxShadow: '0 0 0 1000px rgba(0, 0, 0, 0.3)',
  pointerEvents: 'none',
  zIndex: 10,
};

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0,0,0,0.2)',
  zIndex: 1500,
};

const closeButtonStyle = {
  position: 'absolute',
  top: 8,
  right: 8,
  background: 'transparent',
  border: 'none',
  fontSize: 20,
  cursor: 'pointer',
  color: '#333',
  padding: '4px 8px',
  borderRadius: 4,
  zIndex: 2,
};

export default function InputBarcodeScanner({ 
  value, 
  onChange, 
  label, 
  onKeyDown, 
  required, 
  onSearch,
  ...props 
}) {
  const [showScanner, setShowScanner] = useState(false);
  const [hasDetected, setHasDetected] = useState(false);
  const [isManualInput, setIsManualInput] = useState(false);
  const [scannerError, setScannerError] = useState(null);
  const videoRef = useRef(null);
  const readerRef = useRef(null);

  // Function to check if a string is a URL
  const isUrl = useCallback((text) => {
    try {
      // Check common URL patterns
      const urlPattern = /^(https?:\/\/|www\.|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)[^\s]*$/i;
      return urlPattern.test(text) || text.includes('://') || text.startsWith('www.');
    } catch {
      return false;
    }
  }, []);

  const initializeScanner = useCallback(async () => {
    console.log('Initializing scanner...');
    setScannerError(null);
    
    try {
      // Create new reader instance each time to ensure fresh start
      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.DATA_MATRIX,
        BarcodeFormat.CODE_128,
        BarcodeFormat.QR_CODE,
        BarcodeFormat.EAN_13,
        BarcodeFormat.CODE_39,     // Sometimes used for medical devices
        BarcodeFormat.CODE_93,     // Alternative alphanumeric format
        BarcodeFormat.EAN_8     // For shorter GTINs
      
      ]);

      if (readerRef.current) {
        console.log('Resetting existing reader...');
        await readerRef.current.reset();
      }

      console.log('Creating new reader...');
      const reader = new BrowserMultiFormatReader(hints);
      readerRef.current = reader;

      if (!videoRef.current) {
        console.error('Video element not found');
        setScannerError('Video element not found');
        return;
      }

      console.log('Requesting camera access...');
      const constraints = {
        audio: false,
        video: {
          facingMode: "environment",
          width: { min: 640, ideal: 1280, max: 1920 },
          height: { min: 480, ideal: 720, max: 1080 }
        }
      };

      try {
        // First check if we have camera access
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        stream.getTracks().forEach(track => track.stop()); // Stop the test stream

        console.log('Camera access granted, starting decoder...');
        await readerRef.current.decodeFromConstraints(
          constraints,
          videoRef.current,
          (result, error) => {
            if (result && !hasDetected) {
              console.log('Barcode detected:', result);
              setHasDetected(true);
              const code = result.getText().trim();
              
              if (code) {
                console.log('Processing barcode:', code);
                
                // Check if the scanned code is a URL and ignore it
                if (isUrl(code)) {
                  console.log('Ignoring URL:', code);
                  setHasDetected(false); // Reset to continue scanning
                  return;
                }
                
                if (onChange) {
                  onChange(code);
                }
                onSearch?.(code);
                setShowScanner(false);
              }
            }
            if (error && error.name !== 'NotFoundException') {
              console.error('Scanning error:', error);
            }
          }
        );
      } catch (err) {
        console.error('Camera access error:', err);
        setScannerError(err.message || 'Failed to access camera');
        throw err;
      }
    } catch (error) {
      console.error('Scanner initialization error:', error);
      setScannerError(error.message || 'Failed to initialize scanner');
    }
  }, [onChange, hasDetected, onSearch, isUrl]);

  const stopScanner = useCallback(() => {
    console.log('Stopping scanner...');
    if (readerRef.current) {
      readerRef.current.reset();
      readerRef.current = null;
    }
    setHasDetected(false);
    setScannerError(null);
  }, []);

  const handleInputChange = (e) => {
    setIsManualInput(true);
    onChange?.(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isManualInput) {
      onSearch?.(e.target.value);
    }
    onKeyDown?.(e);
  };

  const handleInputFocus = () => {
    setIsManualInput(true);
  };

  React.useEffect(() => {
    if (showScanner) {
      initializeScanner().catch(error => {
        console.error('Failed to initialize scanner:', error);
        setScannerError(error.message || 'Failed to initialize scanner');
      });
    } else {
      stopScanner();
    }

    return () => {
      stopScanner();
    };
  }, [showScanner, initializeScanner, stopScanner]);

  return (
    <div style={{ position: 'relative', marginBottom: 16 }}>
      <div className="nhsuk-form-group">
        {label && (
          <label className="nhsuk-label" htmlFor="barcode-input">
            {label} {required && <span className="nhsuk-required">*</span>}
          </label>
        )}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            id="barcode-input"
            className="nhsuk-input"
            type="text"
            value={value || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            style={{ paddingRight: '40px' }}
            {...props}
          />
          <button
            type="button"
            aria-label="Scan barcode"
            style={{
              position: 'absolute',
              right: 4,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => {
              setIsManualInput(false);
              setShowScanner(true);
              setScannerError(null);
            }}
          >
            <FaBarcode size={20} color="#333" />
          </button>
        </div>
      </div>
      {showScanner && (
        <>
          <div style={overlayStyle} onClick={() => setShowScanner(false)} />
          <div style={scannerDialogStyle}>
            <button 
              style={closeButtonStyle} 
              onClick={() => {
                setShowScanner(false);
                stopScanner();
              }} 
              aria-label="Close scanner"
            >
              ×
            </button>
            <div style={videoContainerStyle}>
              <video
                ref={videoRef}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                }}
                playsInline // Important for iOS
                muted // Required for auto-play
                autoPlay // Required for the stream to show
              />
              {/* Barcode positioning frame */}
              <div style={barcodeFrameStyle} />
              {scannerError && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'rgba(255, 0, 0, 0.8)',
                  color: 'white',
                  padding: '10px',
                  borderRadius: '5px',
                  textAlign: 'center',
                  maxWidth: '80%'
                }}>
                  {scannerError}
                </div>
              )}
            </div>
            <div style={{ marginTop: 12, textAlign: 'center', color: '#333', fontSize: '14px' }}>
              Hold steady until barcode is detected.
            </div>
            <div style={{ marginTop: 12, textAlign: 'center', color: '#333', fontSize: '14px' }}>
              Align the barcode within the frame
            </div>
          </div>
        </>
      )}
    </div>
  );
}
